/**
 * WorkspaceTemplates.ts - Built-in workspace templates
 * 
 * CHANGE HISTORY:
 * - 251102_1400 danielbeer: Initial creation for universal workspace pattern
 */

import type { WorkspaceConfig } from '../types/WorkspaceTypes';

/**
 * Workspace template definition
 */
export interface WorkspaceTemplate {
  /** Unique template ID */
  id: string;
  
  /** Human-readable template name */
  name: string;
  
  /** Template description */
  description: string;
  
  /** Template category */
  category: 'productivity' | 'development' | 'design' | 'data' | 'communication' | 'custom';
  
  /** Template icon */
  icon?: string;
  
  /** Template tags for search */
  tags?: string[];
  
  /** Template function that generates workspace config */
  create: (params?: any) => WorkspaceConfig;
  
  /** Optional setup steps after workspace creation */
  setup?: (workspace: WorkspaceConfig) => Promise<void>;
  
  /** Required parameters for this template */
  requiredParams?: string[];
  
  /** Optional parameters with defaults */
  optionalParams?: Record<string, any>;
}

/**
 * Built-in workspace templates
 */
export const WORKSPACE_TEMPLATES: Record<string, WorkspaceTemplate> = {
  /**
   * Web Project Template
   */
  'web-project': {
    id: 'web-project',
    name: 'Web Project',
    description: 'Workspace for web development projects with pages, assets, and components',
    category: 'development',
    icon: '🌐',
    tags: ['web', 'development', 'html', 'css', 'javascript'],
    requiredParams: ['name'],
    optionalParams: {
      gitEnabled: true,
      framework: 'react'
    },
    create: (params: { name: string; gitEnabled?: boolean; framework?: string }) => {
      const now = new Date().toISOString();
      
      return {
        id: `web-${Date.now()}`,
        name: params.name,
        description: `Web project: ${params.name}`,
        icon: '🌐',
        views: [
          {
            id: 'pages',
            name: 'Pages',
            type: 'list',
            icon: '📄',
            description: 'All pages in the project',
            dataset: {
              id: 'pages-dataset',
              name: 'Pages',
              description: 'Project pages',
              sources: [
                {
                  name: 'local-pages',
                  type: 'local',
                  config: { path: './pages', pattern: '**/*.html' },
                  entityTypes: ['page'],
                  primary: true,
                  enabled: true
                }
              ],
              createdAt: now,
              updatedAt: now
            },
            createdAt: now,
            updatedAt: now
          },
          {
            id: 'assets',
            name: 'Assets',
            type: 'grid',
            icon: '🖼️',
            description: 'Project assets (images, videos, files)',
            dataset: {
              id: 'assets-dataset',
              name: 'Assets',
              description: 'Project assets',
              sources: [
                {
                  name: 'local-assets',
                  type: 'local',
                  config: { path: './assets', pattern: '**/*' },
                  entityTypes: ['image', 'video', 'file'],
                  primary: true,
                  enabled: true
                }
              ],
              createdAt: now,
              updatedAt: now
            },
            createdAt: now,
            updatedAt: now
          },
          {
            id: 'components',
            name: 'Components',
            type: 'list',
            icon: '🧩',
            description: 'Reusable components',
            dataset: {
              id: 'components-dataset',
              name: 'Components',
              description: 'Project components',
              sources: [
                {
                  name: 'local-components',
                  type: 'local',
                  config: { 
                    path: './components',
                    pattern: params.framework === 'react' ? '**/*.jsx' : '**/*.js'
                  },
                  entityTypes: ['component'],
                  primary: true,
                  enabled: true
                }
              ],
              createdAt: now,
              updatedAt: now
            },
            createdAt: now,
            updatedAt: now
          }
        ],
        defaultViewId: 'pages',
        createdAt: now,
        updatedAt: now
      };
    }
  },

  /**
   * Contact Manager Template
   */
  'contact-manager': {
    id: 'contact-manager',
    name: 'Contact Manager',
    description: 'Workspace for managing contacts with multiple views',
    category: 'productivity',
    icon: '👥',
    tags: ['contacts', 'crm', 'people'],
    requiredParams: ['name'],
    optionalParams: {
      syncGoogle: false,
      syncExchange: false
    },
    create: (params: { name: string; syncGoogle?: boolean; syncExchange?: boolean }) => {
      const now = new Date().toISOString();
      const sources: any[] = [
        {
          name: 'local-contacts',
          type: 'local',
          config: { path: './contacts' },
          entityTypes: ['contact'],
          primary: true,
          enabled: true
        }
      ];
      
      if (params.syncGoogle) {
        sources.push({
          name: 'google-contacts',
          type: 'google',
          config: { service: 'contacts' },
          entityTypes: ['contact'],
          syncMode: 'bidirectional',
          enabled: true
        });
      }
      
      if (params.syncExchange) {
        sources.push({
          name: 'exchange-contacts',
          type: 'exchange',
          config: { service: 'contacts' },
          entityTypes: ['contact'],
          syncMode: 'bidirectional',
          enabled: true
        });
      }
      
      return {
        id: `contacts-${Date.now()}`,
        name: params.name,
        description: `Contact manager: ${params.name}`,
        icon: '👥',
        views: [
          {
            id: 'list',
            name: 'List',
            type: 'list',
            icon: '📋',
            dataset: {
              id: 'contacts-dataset',
              name: 'Contacts',
              sources,
              createdAt: now,
              updatedAt: now
            },
            createdAt: now,
            updatedAt: now
          },
          {
            id: 'cards',
            name: 'Cards',
            type: 'grid',
            icon: '🃏',
            dataset: {
              id: 'contacts-dataset',
              name: 'Contacts',
              sources,
              createdAt: now,
              updatedAt: now
            },
            createdAt: now,
            updatedAt: now
          }
        ],
        defaultViewId: 'list',
        createdAt: now,
        updatedAt: now
      };
    }
  },

  /**
   * Data Analysis Template
   */
  'data-analysis': {
    id: 'data-analysis',
    name: 'Data Analysis',
    description: 'Workspace for data analysis and visualization',
    category: 'data',
    icon: '📊',
    tags: ['data', 'analysis', 'charts', 'visualization'],
    requiredParams: ['name'],
    optionalParams: {
      dataSource: 'local'
    },
    create: (params: { name: string; dataSource?: string }) => {
      const now = new Date().toISOString();
      
      return {
        id: `data-${Date.now()}`,
        name: params.name,
        description: `Data analysis: ${params.name}`,
        icon: '📊',
        views: [
          {
            id: 'table',
            name: 'Table',
            type: 'table',
            icon: '📋',
            dataset: {
              id: 'data-dataset',
              name: 'Data',
              sources: [
                {
                  name: 'data-source',
                  type: params.dataSource as any || 'local',
                  config: { path: './data' },
                  entityTypes: ['record'],
                  primary: true,
                  enabled: true
                }
              ],
              createdAt: now,
              updatedAt: now
            },
            createdAt: now,
            updatedAt: now
          },
          {
            id: 'charts',
            name: 'Charts',
            type: 'custom',
            icon: '📈',
            dataset: {
              id: 'data-dataset',
              name: 'Data',
              sources: [
                {
                  name: 'data-source',
                  type: params.dataSource as any || 'local',
                  config: { path: './data' },
                  entityTypes: ['record'],
                  primary: true,
                  enabled: true
                }
              ],
              createdAt: now,
              updatedAt: now
            },
            uiSettings: {
              chartType: 'bar',
              xAxis: 'category',
              yAxis: 'value'
            },
            createdAt: now,
            updatedAt: now
          }
        ],
        defaultViewId: 'table',
        createdAt: now,
        updatedAt: now
      };
    }
  },

  /**
   * Figma Import Template
   */
  'figma-import': {
    id: 'figma-import',
    name: 'Figma Import',
    description: 'Workspace for importing and managing Figma designs',
    category: 'design',
    icon: '🎨',
    tags: ['figma', 'design', 'import', 'assets'],
    requiredParams: ['name', 'fileKey', 'token'],
    create: (params: { name: string; fileKey: string; token: string }) => {
      const now = new Date().toISOString();
      
      return {
        id: `figma-${Date.now()}`,
        name: params.name,
        description: `Figma import: ${params.name}`,
        icon: '🎨',
        views: [
          {
            id: 'frames',
            name: 'Frames',
            type: 'grid',
            icon: '🖼️',
            dataset: {
              id: 'figma-frames',
              name: 'Figma Frames',
              sources: [
                {
                  name: 'figma-api',
                  type: 'figma',
                  config: {
                    fileKey: params.fileKey,
                    token: params.token
                  },
                  entityTypes: ['frame', 'component'],
                  primary: true,
                  enabled: true
                }
              ],
              createdAt: now,
              updatedAt: now
            },
            createdAt: now,
            updatedAt: now
          },
          {
            id: 'assets',
            name: 'Assets',
            type: 'grid',
            icon: '📦',
            dataset: {
              id: 'figma-assets',
              name: 'Figma Assets',
              sources: [
                {
                  name: 'figma-images',
                  type: 'figma',
                  config: {
                    fileKey: params.fileKey,
                    token: params.token
                  },
                  entityTypes: ['image', 'asset'],
                  primary: true,
                  enabled: true
                }
              ],
              createdAt: now,
              updatedAt: now
            },
            createdAt: now,
            updatedAt: now
          }
        ],
        defaultViewId: 'frames',
        createdAt: now,
        updatedAt: now
      };
    }
  },

  /**
   * Empty Template
   */
  'empty': {
    id: 'empty',
    name: 'Empty Workspace',
    description: 'Start with a blank workspace',
    category: 'custom',
    icon: '📝',
    tags: ['empty', 'blank', 'custom'],
    requiredParams: ['name'],
    create: (params: { name: string }) => {
      const now = new Date().toISOString();
      
      return {
        id: `workspace-${Date.now()}`,
        name: params.name,
        description: params.name,
        icon: '📝',
        views: [],
        createdAt: now,
        updatedAt: now
      };
    }
  }
};

/**
 * Workspace Template Manager
 */
export class WorkspaceTemplateManager {
  /**
   * Get all available templates
   */
  static getTemplates(): WorkspaceTemplate[] {
    return Object.values(WORKSPACE_TEMPLATES);
  }
  
  /**
   * Get templates by category
   */
  static getTemplatesByCategory(category: WorkspaceTemplate['category']): WorkspaceTemplate[] {
    return this.getTemplates().filter(t => t.category === category);
  }
  
  /**
   * Get template by ID
   */
  static getTemplate(id: string): WorkspaceTemplate | null {
    return WORKSPACE_TEMPLATES[id] || null;
  }
  
  /**
   * Search templates by name or tags
   */
  static searchTemplates(query: string): WorkspaceTemplate[] {
    const lowerQuery = query.toLowerCase();
    return this.getTemplates().filter(t => 
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
    );
  }
  
  /**
   * Create workspace from template
   */
  static async createFromTemplate(
    templateId: string,
    params: any
  ): Promise<WorkspaceConfig> {
    const template = this.getTemplate(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    // Validate required params
    if (template.requiredParams) {
      for (const param of template.requiredParams) {
        if (!(param in params)) {
          throw new Error(`Missing required parameter: ${param}`);
        }
      }
    }
    
    // Merge with optional params
    const mergedParams = {
      ...template.optionalParams,
      ...params
    };
    
    // Generate workspace config
    const workspace = template.create(mergedParams);
    
    // Run setup if provided
    if (template.setup) {
      await template.setup(workspace);
    }
    
    return workspace;
  }
  
  /**
   * Register custom template
   */
  static registerTemplate(template: WorkspaceTemplate): void {
    if (WORKSPACE_TEMPLATES[template.id]) {
      throw new Error(`Template already exists: ${template.id}`);
    }
    WORKSPACE_TEMPLATES[template.id] = template;
  }
  
  /**
   * Unregister template
   */
  static unregisterTemplate(id: string): boolean {
    if (!WORKSPACE_TEMPLATES[id]) {
      return false;
    }
    delete WORKSPACE_TEMPLATES[id];
    return true;
  }
  
  /**
   * Export template as JSON
   */
  static exportTemplate(id: string): string {
    const template = this.getTemplate(id);
    if (!template) {
      throw new Error(`Template not found: ${id}`);
    }
    
    // Can't serialize functions, so export metadata only
    const exportData = {
      id: template.id,
      name: template.name,
      description: template.description,
      category: template.category,
      icon: template.icon,
      tags: template.tags,
      requiredParams: template.requiredParams,
      optionalParams: template.optionalParams
    };
    
    return JSON.stringify(exportData, null, 2);
  }
}
